import { formatDate, parseDate } from '@progress/kendo-angular-intl';
import { FieldDataType } from './shared/field.model';
import { Config } from './config.model';
import formatDistance from 'date-fns/formatDistance';

export class Utils {
    static compareNumbers(a: number, b: number): number {
        if (a < b) {
            return -1;
        }

        if (a > b) {
            return 1;
        }

        return 0;
    }

    static isMobile(): boolean {
        return window.matchMedia(this.isMobileMediaQuery()).matches;
    }

    static isMobileMediaQuery() {
        return "(hover: none)";
    }

    static mimeTypeToIcon(type: string): string {
        const index = type.lastIndexOf(".");
        let extension: string = type;
        if (index != undefined && index >= 0 && type.length > 1) {
            extension = type.substr(index + 1);
        }

        switch (extension.toLocaleLowerCase()) {
            case 'doc':
            case 'docx':
                return "./assets/images/mimetypes/mime_microsoft_word32.svg";

            case 'xls':
            case 'xlsx':
                return "./assets/images/mimetypes/mime_microsoft_excel32.svg";

            case 'ppt':
            case 'pptx':
                return "./assets/images/mimetypes/mime_microsoft_powerpoint32.svg";

            case 'pdf':
                return "./assets/images/mimetypes/mime_pdf.svg";

            case 'gif':
                return "./assets/images/mimetypes/mime_gif.svg";

            case 'jpg':
            case 'jpeg':
            case 'jpe':
            case 'jif':
            case 'jfif':
            case 'jfi':
                return "./assets/images/mimetypes/mime_jpeg.svg";

            case 'png':
                return "./assets/images/mimetypes/mime_png.svg";

            case 'zip':
            case 'sitx':
            case '7z':
            case 'rar':
            case 'gz':
                return "./assets/images/mimetypes/mime_zip.svg";

            case 'msg':
            case 'eml':
                return "./assets/images/mimetypes/mime_mail.svg";

            case 'tif':
            case 'tiff':
            case 'svg':
                return "./assets/images/mimetypes/mime_image.svg";

            default:
                return "./assets/images/mimetypes/mime_document.svg";

        }
    }

    static getPath(fullPath: string): string {
        const index = fullPath.lastIndexOf("/");
        let returnPath: string = fullPath.substring(0, index + 1);
        return returnPath;
    }

    /**
     * The web server expects Dates to be in the Microsoft form '/Date({timestamp of date}{timezone})/'
     * All APIs should convert all dates to this format on the way back to the server
     * @param date - Date to convert
     */
    static getDateToPost(date: Date): string {
        return `/Date(${date.getTime().toString()}${formatDate(date, 'Z')})/`;
    }

    static getDateToGet(date: Date | null): string {
        if (date !== null) {
            // Offset date by the current timezone offset
            return new Date(date.getTime() + (date.getTimezoneOffset() * -1 * 60000)).toJSON();
        }
        return '';
    }

    /**
     * Get a string representing the date with respect to the current date
     * (e.g. "about 2 days ago")
     */
    static fromNow(date: Date): string {
        return formatDistance(date, new Date(), { addSuffix: true });
    }

    /**
    * Converts data to a typed value
    * *Currently only supports date fields.*
    * 
    * @param field Field type
    * @param value The value to be converted to be typed
    * @param config If provided, configuration model for applying typed values
    */
    static useTypedValue(type: FieldDataType, value: any, config?: Config): any {
        // If the value can be used a date, use it. Otherwise use the original value.
        let valueDate = this.checkForAndParseDate(type, value, config);
        if (valueDate) {
            return valueDate;
        }

        return value;
    }

    /**
     * Converts a string to a date value.
     * 
     * @param type Field type
     * @param value The value to be converted to a date
     * @param config If provided, configuration model for applying typed values
     */
    static checkForAndParseDate(type: FieldDataType, value: any, config?: Config) {

        switch (type) {
            case FieldDataType.Date:
            case FieldDataType.DateTime:
            case FieldDataType.Time:

                //
                // First try to parseDate using standard date parsing
                // https://github.com/telerik/kendo-intl/blob/develop/docs/date-parsing/index.md
                //
                const parsedDate = parseDate(value);

                //
                // If these fail, and if a config model is provided, use the fallback
                // parse formatting value from appConfig for the specific field type.
                //
                if (parsedDate === null && config) {

                    switch (type) {
                        case FieldDataType.Date:
                            if (config.dateParseFormatFallback.length > 0)
                                return parseDate(value, config.dateParseFormatFallback);
                            return parsedDate;
                        case FieldDataType.DateTime:
                            if (config.dateTimeParseFormatFallback.length > 0)
                                return parseDate(value, config.dateTimeParseFormatFallback);
                            return parsedDate;
                        case FieldDataType.Time:
                            if (config.timeParseFormatFallback.length > 0)
                                return parseDate(value, config.timeParseFormatFallback);
                            return parsedDate;
                    }
                }

                return parsedDate;
        }

        return null;
    }

    /**
     * Download a blob to a file in the browser 
     * @param data Blob data fo file to dlownload
     * @param fileName File name only
     */
    static downloadBlob(data: any, fileName: string) {
        // Create an invisible A element
        console.log(`Function entry : downloadBlob : ${fileName}`)
        if (navigator.msSaveOrOpenBlob) {
            console.log(`Function : downloadBlob : msSaveOrOpenBlob`)
            navigator.msSaveOrOpenBlob(data, fileName);
        } else {
            console.log(`Function : downloadBlob : createElement`)
            const a = document.createElement("a");
            a.style.display = "none";
            document.body.appendChild(a);

            // Set the HREF to a Blob representation of the data to be downloaded
            a.href = window.URL.createObjectURL(
                new Blob([data], {})
            );

            // Use download attribute to set set desired file name
            a.setAttribute("download", fileName);

            // Trigger the download by simulating click
            a.click();

            // Cleanup
            window.URL.revokeObjectURL(a.href);
            document.body.removeChild(a);
        }
    }
}
